import { Col, Container } from "@amzn/stencil-react-components/layout";
import { Button, ButtonSize, ButtonVariant } from "@amzn/stencil-react-components/button";
import { WithModal } from "@amzn/stencil-react-components/modal";
import { Text } from "@amzn/stencil-react-components/text";
import { getString } from "@/common/uistringlabels/ui-string-utils";
import { IItemVersion } from "@/common/types/item.types";
import { ProductImage } from "@/common/components/ProductImage/product-image";
import { formatPrice } from "@/common/helpers/price.utils";
import { ChangeVersionModal } from "@/pages/Checkout/components/ChangeVersionModal/change-version-modal";
import React from "react";

export interface CheckoutItemSidePanelProps {
  versions: IItemVersion[];
  parentName?: string;
  selectedVersion: IItemVersion;
  onVersionChange: (version: IItemVersion) => void;
}

export const CheckoutItemSidePanel: React.FC<CheckoutItemSidePanelProps> = ({
  versions,
  parentName,
  selectedVersion,
  onVersionChange,
}) => {
  const displayName = parentName || selectedVersion.name;

  return (
    <Col backgroundColor="neutral05" width={367} data-testid="checkout-item-side-panel">
      <Container paddingHorizontal={40} paddingTop={32} isFullWidth={true}>
        <Col gridGap="S400" width="100%">
          {/* Product image */}
          <ProductImage
            logoUrl={selectedVersion.logoUrl}
            altText={`${displayName} logo`}
            size={110}
            dataTestId="checkout-product-image"
          />

          <Col gridGap="S300">
            {/* Product name */}
            <Text color="neutral90" fontSize="T200" fontWeight="bold" data-testid="checkout-product-name">
              {displayName}
            </Text>

            {/* Price */}
            <Col>
              <Text color="neutral70" fontSize="T200">
                {getString("checkout.sidePanel.price")}
              </Text>
              <Text color="neutral90" fontSize="T200" fontWeight="bold" data-testid="checkout-product-price">
                {formatPrice(selectedVersion.amount, selectedVersion.currency)}
              </Text>
            </Col>

            {/* Version */}
            <Col>
              <Text color="neutral70" fontSize="T200">
                {getString("checkout.sidePanel.version")}
              </Text>
              <Text color="neutral90" fontSize="T200" fontWeight="bold" data-testid="checkout-product-version">
                {selectedVersion.displayText}
              </Text>
            </Col>

            {/* Change version button */}
            {versions.length > 1 && (
              <WithModal
                renderModal={({ close }) => (
                  <ChangeVersionModal
                    close={close}
                    versions={versions}
                    selectedVersion={selectedVersion}
                    onConfirm={onVersionChange}
                  />
                )}
                isScrollable={false}
              >
                {({ open }) => (
                  <Button
                    variant={ButtonVariant.Tertiary}
                    size={ButtonSize.Small}
                    onClick={open}
                    dataTestId="change-version-button"
                  >
                    {getString("checkout.sidePanel.changeVersion")}
                  </Button>
                )}
              </WithModal>
            )}
          </Col>
        </Col>
      </Container>
    </Col>
  );
};
